import { render } from 'preact';

import { namespace } from '../helpers/namespaces';
import { getClientHost, determineTenant } from '../helpers/client';
import { hexToRGB } from '../helpers/colours';
import { applyThemeFromEmbedConfig, parseThemeConfig } from './theme-config';
import PlayerOfTheGameComponent from './potg.component';

import { getLanguageAndQueryParams } from '../helpers/language';

import { DataCaptureForm as Form } from 'playbook-data-capture-sdk-web';
import { sortPollOptions } from './potg.helpers';
import { I18nextProvider } from 'react-i18next';
import i18nInstance from '../i18n';

const ID_DATA_ATTRIBUTE = `data-${namespace}potg`;
const ID_DATA_CAPTURE_FORM = `data-${namespace}form`;
const TENANT_DATA_ATTRIBUTE = `data-${namespace}tenant`;

const PREVIEW_DATA_ATTRIBUTE = `${ID_DATA_ATTRIBUTE}-preview`;
const INIT_ATTR = 'data-fek-initialised';
const NO_INIT_SELECTOR = `:not([${INIT_ATTR}])`;

/**
 * Class which goes through page and finds FEK polls to embed
 */
export default class PlayerOfTheGame {
    constructor() {
        this.init();
        this.observeDOM();
    }

    init() {
        document
            .querySelectorAll(`[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`)
            .forEach((item) => this.initNewPOTG(item, false));
    }

    initNewPOTG(POTGEmbed) {
        const embedTenant = POTGEmbed.getAttribute(TENANT_DATA_ATTRIBUTE);
        determineTenant(embedTenant);

        const POTG_ID = POTGEmbed.getAttribute(ID_DATA_ATTRIBUTE);
        POTGEmbed.setAttribute(INIT_ATTR, '');

        const matchId = POTGEmbed.dataset.fekMatchId;
        if (POTG_ID === 'dynamic' && matchId) {
            this.getPollIdByMatchId(matchId)
                .then((data) => {
                    if (data.potg_poll) {
                        this.getPOTGData(POTGEmbed, data.potg_poll);
                    } else {
                        console.warn(
                            'Playbook POTG embed: No poll found for match ID',
                            matchId
                        );
                    }
                })
                .catch((error) => {
                    console.warn('Playbook POTG embed error', error);
                });
            return;
        }
        this.getPOTGData(POTGEmbed, POTG_ID);
    }

    initPreviews(onPreviewEditChange) {
        document
            .querySelectorAll(`[${PREVIEW_DATA_ATTRIBUTE}]`)

            .forEach((pollPreviewEmbed) => {
                const POTGData = JSON.parse(
                    pollPreviewEmbed.getAttribute(PREVIEW_DATA_ATTRIBUTE)
                );
                this.renderEmbed(
                    pollPreviewEmbed,
                    POTGData,
                    true,
                    onPreviewEditChange
                );
            });
    }

    /**
     * Sets up a MutationObserver to check the DOM
     * if new poll embeds have been dynamically added
     */
    observeDOM() {
        const observer = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const POTGEmbed = node.querySelector(
                            `[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`
                        );
                        if (POTGEmbed) {
                            this.initNewPOTG(POTGEmbed);
                        }
                    }
                });
            });
        });

        observer.observe(document.body, { subtree: true, childList: true });
    }

    /**
     * Function that fetches the poll ID by match ID
     *
     * @param {string|number} matchId the match ID
     * @returns
     */
    getPollIdByMatchId(matchId) {
        const endpoint = `https://${getClientHost()}${
            process.env.API_SDP_ADAPTOR_API
        }/matches/${matchId}`;

        return fetch(endpoint)
            .then((response) => response.json())
            .catch((error) => {
                console.warn(
                    'Playbook POTG embed error fetching match data',
                    error
                );
            });
    }

    /**
     * Fetches poll data from API
     *
     * @param {Element} container - embed container
     * @param {string} POTG_ID - FEK poll ID
     */
    getPOTGData(container, POTG_ID) {
        const { queryParams } = getLanguageAndQueryParams();

        const endpoint = `https://${getClientHost()}${
            process.env.API_PATH
        }/polls/${POTG_ID}${queryParams}`;

        fetch(endpoint)
            .then((response) => {
                if (!response.ok) {
                    console.warn('Playbook POTG embed unsuccessful', POTG_ID);
                    return null;
                }

                return response.json();
            })
            .then((POTGData) => {
                if (!POTGData) {
                    return;
                }

                sortPollOptions(POTGData);

                this.renderEmbed(container, POTGData, false, null);
            })
            .catch((error) => {
                console.warn('Playbook POTG embed unsuccessful', error);
            });
    }

    /**
     * Renders embeds to DOM
     *
     * @param {Element} container - embed container
     * @param {object} POTGData - poll data
     * @param {boolean} isPreview - poll embed is a preview
     * @param {Function} onPreviewEditChange callback function when contenteditable data changes
     */
    async renderEmbed(container, POTGData, isPreview, onPreviewEditChange) {
        /**
         * Used to fetch poll data to get latest vote data
         *
         * @returns {Promise} fetch promise
         */

        // Check if there is a linked form
        const embeddedFormId =
            container.firstElementChild?.getAttribute(ID_DATA_CAPTURE_FORM);
        let embeddedFormData;
        if (embeddedFormId) {
            embeddedFormData = await Form.initEmbeddableNewForm(embeddedFormId)
                .then((formData) => formData)
                .catch((rejectValue) => rejectValue);
        }

        const refetch = (POTGData) => {
            const { queryParams } = getLanguageAndQueryParams();

            return fetch(
                `https://${getClientHost()}${
                    process.env.API_PATH
                }/polls/${POTGData.poll_id}${queryParams}`
            ).then((response) => response.json());
        };

        // Apply optional per-embed theme before render.
        applyThemeFromEmbedConfig(container, parseThemeConfig(container));

        // Set BG colour and gradient overrides.
        const hexBgColour = POTGData?.background_colour;
        const rgbBgColour = hexBgColour ? hexToRGB(hexBgColour) : null;

        const colourOverrides = {
            hex: hexBgColour,
            rgb: rgbBgColour
        };

        const shadow =
            container.shadowRoot || container.attachShadow({ mode: 'open' });

        render(
            <I18nextProvider i18n={i18nInstance}>
                <PlayerOfTheGameComponent
                    data={POTGData}
                    container={container}
                    embeddedFormData={embeddedFormData}
                    isPreview={isPreview}
                    onPreviewEditChange={onPreviewEditChange}
                    refetch={() => refetch(POTGData)}
                    colourOverrides={colourOverrides}
                />
            </I18nextProvider>,
            shadow
        );
    }
}
